if (!customElements.get('form-wizard')) {
  customElements.define(
    'form-wizard',
    class FormWizard extends HTMLElement {
      constructor() {
        super();

        /**
         * @type {NodeListOf<HTMLElement>}
         */
        this.steps = this.querySelectorAll('[js-form-wizard-step]');
        this.scrollContainer = document.querySelector('[js-form-wizard-scroll-container]');
        this.scrollToBottom = document.querySelector('[js-form-wizard-el="scroll-to-bottom"]');
        this.containers = this.querySelectorAll('[js-form-wizard-step-container]');
        this.stepsMedia = this.querySelectorAll('[js-form-wizard-step-media]');
        this._scrollTicking = false;
        this._currentActiveStep = null;
      }

      connectedCallback() {
        this.steps.forEach((step) => {
          step.addEventListener('change', this.handleChange.bind(this, step));
        });

        this.scrollContainer?.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        this.scrollToBottom?.addEventListener('click', () => {
          this.scrollContainer?.scrollTo({
            top: this.scrollContainer.scrollHeight,
            behavior: 'smooth',
          });
        });

        document.addEventListener('resize', this.setScrollbarVar.bind(this), { passive: true });

        this.setScrollbarVar();
        this.handleDisabledStepClick();
      }

      /**
       * @param {HTMLElement} step
       */
      handleChange(step) {
        if (this.nextStep(step) === null) return;

        if (this.stepIsValid(step)) {
          const nextStep = /** @type {HTMLElement | null} */ (this.nextStep(step));

          nextStep?.removeAttribute('disabled');
          nextStep?.hasAttribute('js-form-wizard-auto-select') && this.autoSelectFirstOption(nextStep);
        } else {
          this.nextStep(step)?.setAttribute('disabled', 'true');
        }
      }

      handleScroll() {
        // Use requestAnimationFrame to batch DOM reads/writes and prevent jank
        if (this._scrollTicking) return;

        this._scrollTicking = true;
        requestAnimationFrame(() => {
          this._updateActiveStep();
          this._scrollTicking = false;
        });
      }

      _updateActiveStep() {
        let newActiveStep = null;

        this.steps.forEach((step) => {
          // Skip hidden steps (aria-hidden="true")
          if (step.getAttribute('aria-hidden') === 'true') {
            return;
          }

          const rect = step.getBoundingClientRect();

          if (rect.top <= 300 && rect.bottom > 300) {
            newActiveStep = step;
          }
        });

        // Only update DOM if active step changed
        if (newActiveStep !== this._currentActiveStep) {
          // Deactivate previous step
          if (this._currentActiveStep) {
            this._currentActiveStep.setAttribute('js-form-wizard-step', '');
          }

          // Activate new step
          if (newActiveStep) {
            newActiveStep.setAttribute('js-form-wizard-step', 'active');

            /**
             * @type {HTMLVideoElement | null}
             */
            const video = newActiveStep.querySelector('.form-wizard__step-media video');
            video && video.paused && video.play();
          }

          this._currentActiveStep = newActiveStep;
        }
      }

      handleDisabledStepClick() {
        this.containers.forEach((container) => {
          container.addEventListener('click', () => {
            const formGroupIsDisabled = container.querySelector('[js-form-wizard-step]')?.hasAttribute('disabled');

            if (formGroupIsDisabled) {
              const firstDisabledElement = Array.from(this.containers).find((el) =>
                el.querySelector('[js-form-wizard-step]')?.hasAttribute('disabled'),
              );

              if (firstDisabledElement) {
                const previousElementSibling = /** @type {HTMLElement | null} */ (
                  firstDisabledElement.previousElementSibling
                );

                if (previousElementSibling) {
                  this.scrollContainer?.scrollTo({
                    top: previousElementSibling.offsetTop - 60,
                    behavior: 'smooth',
                  });
                }
              }
            }
          });
        });
      }

      /**
       * @param {HTMLElement} step
       */
      nextStep(step) {
        let nextContainer = step.closest('[js-form-wizard-step-container]')?.nextElementSibling;

        while (nextContainer) {
          // Check if this is a step container
          if (nextContainer.hasAttribute('js-form-wizard-step-container')) {
            const nextStep = nextContainer.querySelector('[js-form-wizard-step]');
            // Return this step if it exists and is not hidden
            if (nextStep && nextStep.getAttribute('aria-hidden') !== 'true') {
              return nextStep;
            }
          }
          // Move to next sibling container
          nextContainer = nextContainer.nextElementSibling;
        }
        return null;
      }

      /**
       * @param {HTMLElement} step
       */
      stepInputs(step) {
        return Array.from(step.querySelectorAll('input:not([type="hidden"]), select')).filter((input) => {
          return !input.closest('[aria-hidden="true"]');
        });
      }

      /**
       * @param {HTMLElement} step
       */
      stepIsValid(step) {
        let isValid = true;

        this.stepInputs(step).forEach((input) => {
          if (input.type === 'radio') {
            if (!this.isOptionGroupValid(input.name)) {
              isValid = false;
            }
          } else {
            if (input.value === '-1' && this.inputIsVisible(input)) {
              isValid = false;
            }
          }
        });

        return isValid;
      }

      /**
       * @param {HTMLInputElement} input
       */
      inputIsVisible(input) {
        return input.offsetHeight > 0 || !input.getAttribute('js-form-validate-hidden');
      }

      /**
       * @param {HTMLElement | null} step
       */
      autoSelectFirstOption(step) {
        if (!step) return;

        const firstOption = /** @type {HTMLInputElement | null} */ (step.querySelector('input[type="radio"]'));

        firstOption?.click();
      }

      /**
       * @param {String} name
       */
      isOptionGroupValid(name) {
        return this.querySelector(`input[name="${name}"]:checked`) !== null;
      }

      setScrollbarVar() {
        const el = document.createElement('div');

        el.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
        document.body.appendChild(el);

        const scrollbarWidth = el.offsetWidth - el.clientWidth;

        el.remove();

        document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
      }
    },
  );
}
